import { BaseParser, IValidateParams } from './BaseParser';
import { ParseResult, IParseResult } from '../FormatterTypes';
import {
  formatterStrParamDef,
  formatterBoolParamDef,
} from '../Utils/ParamUtils';
import { padToLength } from '../../stringUtils';
import { getControlDigit } from '../Utils/StringUtils';

const numbers = '0123456789';

class TEDParsePersNr extends BaseParser {
  public static getInstance(): TEDParsePersNr {
    return this.instance ? this.instance : (this.instance = new this());
  }

  private static instance: TEDParsePersNr;

  defaultParams(): string {
    return `ValidPNSeps="-/ ,.";CenturyLimit="+0";AssumeCentury="Y";RequireCentury="N";DefMonth="";DefDay="";AllowIncomplete="Y";AllowSFU="N";AllowZero="N";AllowZeroNumber="N";Allow3DigitNumber="Y";AllowPlusSep="Y";AllowDateFormat="N";`;
  }

  getValidateParams(params: string): IValidateParams {
    const allowPlusSep = formatterBoolParamDef(
      'AllowPlusSep',
      params,
      this.defaultParams()
    );
    const validPNSeps = `${formatterStrParamDef(
      'ValidPNSeps',
      params,
      this.defaultParams()
    )}${allowPlusSep ? '+' : ''}`;

    return { inputsList: numbers, separators: validPNSeps };
  }

  parse(text: string, params: string = ''): IParseResult {
    if (!text) {
      return { value: null, result: ParseResult.OK };
    }

    const pattern = this.makeRegEx(text, params);
    if (pattern) {
      const parts = text.match(pattern)?.filter(x => x);
      if (!parts) {
        return { value: text, result: ParseResult.ERROR_PATTERN };
      }
      const { value, result } = this.interpretParts(text, parts, params);
      return { value: value ?? null, result: result };
    }

    return { value: text, result: ParseResult.ERROR };
  }

  makeRegEx(text: string, params: string = ''): RegExp | null {
    let validSeps = formatterStrParamDef(
      'ValidPNSeps',
      params,
      this.defaultParams()
    );
    const allowPlusSep = formatterBoolParamDef(
      'AllowPlusSep',
      params,
      this.defaultParams()
    );
    const allowIncomplete = formatterBoolParamDef(
      'allowIncomplete',
      params,
      this.defaultParams()
    );
    const allow3DigitNumber = formatterBoolParamDef(
      'Allow3DigitNumber',
      params,
      this.defaultParams()
    );
    const requireCentury = formatterBoolParamDef(
      'RequireCentury',
      params,
      this.defaultParams()
    );
    const assumeCentury = formatterBoolParamDef(
      'AssumeCentury',
      params,
      this.defaultParams()
    );
    const allowDateFormat = formatterBoolParamDef(
      'AllowDateFormat',
      params,
      this.defaultParams()
    );
    let pattern = '';
    let numberPattern = '(\\d{4})?';
    if (allowPlusSep) {
      if (!validSeps.includes('+')) {
        validSeps += '+';
      }
    }
    if (allowIncomplete && allow3DigitNumber) {
      numberPattern = '((?:\\d{4})|(?:\\d{3}))?';
    }
    if (requireCentury) {
      if (allowIncomplete) {
        pattern = `(\\d{4})(?:(?:\\d{2})|(?:[1-9]))?(?:(\\d{2}))?[${validSeps}]?${numberPattern}`;
      } else {
        pattern = `(\\d{4})((?:\\d{2})|(?:[1-9]))(\\d{2})[${validSeps}]?${numberPattern}`;
      }
    } else if (assumeCentury) {
      const firstYearInterval =
        text.length === 10 || text.length === 11 ? 2 : 4;
      const secondYearInterval = firstYearInterval === 4 ? 2 : 4;
      if (allowIncomplete) {
        pattern = `(?:(\\d{${firstYearInterval}})|(\\d{${secondYearInterval}}))(?:((?:\\d{2})|(?:[1-9]))(?:(\\d{2})[${validSeps}]?${numberPattern})?)?`;
      } else {
        pattern = `(?:(\\d{2})|(\\d{4}))((?:\\d{2})|(?:[1-9]))(\\d{2})[${validSeps}]?${numberPattern}`;
      }
    } else {
      if (allowIncomplete) {
        pattern = `((?:\\d{2})|(?:\\d{4}))(?:(?:\\d{2})|(?:[1-9]))?(?:(\\d{2}))?[${validSeps}]?${numberPattern}`;
      } else {
        pattern = `((?:\\d{2})|(?:\\d{4}))((?:\\d{2})|(?:[1-9]))(\\d{2})[${validSeps}]?${numberPattern}`;
      }
    }
    if (allowDateFormat) {
      pattern = `((?:\\d{2})|(?:\\d{4}))-((?:\\d{2})|(?:[1-9]))-((?:\\d{2})|(?:[1-9]))${pattern}`;
    }

    return new RegExp(`^${pattern}$`);
  }

  interpretParts(
    text: string,
    regexResult: string[],
    params: string
  ): { result: ParseResult; value?: string } {
    const plusSep = text.includes('+');
    const centuryLimit = formatterStrParamDef(
      'CenturyLimit',
      params,
      this.defaultParams()
    );

    let year = regexResult[1] ?? '';
    if (year.length == 2) {
      let y = Number.parseInt(year);
      let cl = new Date().getFullYear() + 20;
      if (centuryLimit) {
        const centuryNumber = Number.parseInt(centuryLimit);
        if (['+', '-'].indexOf(centuryLimit[0]) !== -1 && y) {
          cl = new Date().getFullYear() + centuryNumber;
        } else if (y) {
          if (centuryNumber >= 1000) {
            cl = centuryNumber;
          } else {
            cl = new Date().getFullYear() + centuryNumber;
          }
        }
      }
      y = cl - (cl % 100) + y;
      if (y > cl) {
        y -= 100;
      }
      if (plusSep) {
        y -= 100;
      }

      year = y.toString();
    }

    const month = regexResult[2] ? padToLength(regexResult[2], 2, '0') : '';
    const day = regexResult[3] ?? '';
    const special = regexResult[4] ?? '';
    const date = new Date(
      `${year}${month ? `-${month}` : ''}${day ? `-${day}` : ''}`
    );
    if (isNaN(date.valueOf()) || date > new Date()) {
      return { result: ParseResult.ERROR };
    }
    const value = `${year}${month}${day}${special}`;
    if (special && getControlDigit(value) !== special[special.length - 1]) {
      return { result: ParseResult.ERROR_ON_INVALIDCONTROLDIGIT };
    }
    return { value: value, result: ParseResult.OK };
  }
}

export { TEDParsePersNr };
